import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Input } from "@mui/material";
import { t } from "i18next";
import { Close } from "@mui/icons-material";

import {
  removeAndAddMessages,
  getAppSSID,
  getConversationID,
  selectPropertyId,
  getOrgId,
  selectIsFaq,
  addNewMessage,
  selectIsDisabled,
  setIsDisabled,
  getEnabledScheduleTour,
  selectAPIKey,
  selectPerformanceReport,
  setShowAllOptions,
  getShowAllOptions,
  getHeaderBgColor,
  setLabelItems,
  setAMAReply,
  selectMessages,
  selectLabelItems,
  getVisibleInPersonTour,
  getVisibleLiveVideoTour,
  getVisibleSelfGuidedTour,
  setShouldFetchSlots,
} from "slices";
import { Icon, CustomTooltip, OptionsBox } from "components";
import { getFAQsReply, ALL_CHAT_SUBJECTS, reportIds, optionsBySubject, handleTourResponse } from "utils";
import { DEFAULT_SESSION_ID, FOLLOW_UP_KEYS, FORM_4_CONTACT_US_OPTIONS, INITIAL_REPLIES } from "utils/constants/index";
import { setIsFetchManagement, setReplies } from "slices/replies-slice/index";
import { getEntryId, getPriceFilter, setAPIKey } from "slices/property-slice/index";
import { fetchPropertyData, getLastConversations } from "utils/methods/index";
import { setSearchResult } from "slices/units-slice/index";

const AMAOptions = () => {
  const dispatch = useDispatch();
  const enabledScheduleTour = useSelector(getEnabledScheduleTour);
  const messages = useSelector(selectMessages);

  const SSID = useSelector(getAppSSID);
  const conversationID = useSelector(getConversationID);
  const propertyId = useSelector(selectPropertyId);
  const orgId = useSelector(getOrgId);
  const apiKey = useSelector(selectAPIKey);
  const isFaq = useSelector(selectIsFaq);
  const optionDisabled = useSelector(selectIsDisabled);
  const isPerformanceReport = useSelector(selectPerformanceReport);
  const showAllOptions = useSelector(getShowAllOptions);
  const backgroundColor = useSelector(getHeaderBgColor);
  const labelItems = useSelector(selectLabelItems);
  const isVideoTourEnabled = useSelector(getVisibleLiveVideoTour);
  const isInPersonTourEnabled = useSelector(getVisibleInPersonTour);
  const isSelfGuidedTourEnabled = useSelector(getVisibleSelfGuidedTour);
  const isPriceFilter = useSelector(getPriceFilter);
  const entryId = useSelector(getEntryId);

  const [inputValue, setInputValue] = useState("");
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    if (inputValue === "") {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [inputValue, isFaq]);

  const onChange = (e) => {
    setInputValue(e.target.value);
  };

  async function getScheduleStatus() {
    const url = `${process.env.REACT_APP_API_BASE_URL}/apartments/schedule_status?property_id=${propertyId}&api_key=${apiKey}&entry_session_id=${SSID}`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();

      return data;
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
      throw error;
    }
  }

  const onFAQs = (propertyId) => {
    dispatch(
      addNewMessage([
        { message: inputValue, speaker: "user", createdAt: new Date().toISOString() },
        { message: `typing`, speaker: "bot", createdAt: new Date() },
      ])
    );

    setInputValue("");
    dispatch(setIsDisabled(true));

    const history = getLastConversations(messages, 5)
      ?.map((i) => ({
        role: i.speaker == "bot" || i.speaker == "faqBot" ? "assistant" : "user",
        content: i.message,
      }))
      .filter((i) => i.content && i.content.trim() !== "");

    const faqReply = {
      history,
      propertyId,
      orgId: orgId,
      message: inputValue,
      conversation_id: conversationID || "",
      session_id: SSID || DEFAULT_SESSION_ID,
      graphql_api_key: encodeURIComponent(apiKey),
      tourScheduleEnabled: enabledScheduleTour,
    };

    if (entryId) {
      faqReply["entry_id"] = entryId;
    }

    getFAQsReply(faqReply)
      .then((res) => {
        handleResponse(res);
      })
      .catch(async (err) => {
        if (
          [403, 401].includes(err?.response?.status) || // See if response status is 401 or 403
          ["401 Unauthorized", "API Key is expired!", "API key is Invalid!"].includes(err?.response?.data?.detail)
        ) {
          dispatch(setIsFetchManagement(false));

          let api_key = apiKey;

          await fetchPropertyData(propertyId).then((res) => {
            api_key = res?.data?.api_key;
            dispatch(setAPIKey(api_key));
          });

          const faqReply2 = {
            history,
            propertyId,
            orgId: orgId,
            message: inputValue,
            conversation_id: conversationID || "",
            session_id: SSID || DEFAULT_SESSION_ID,
            graphql_api_key: encodeURIComponent(api_key),
            tourScheduleEnabled: enabledScheduleTour,
          };

          if (entryId) {
            faqReply2["entry_id"] = entryId;
          }

          getFAQsReply(faqReply2)
            .then((res) => {
              handleResponse(res);
            })
            .catch((err) => {
              dispatch(removeAndAddMessages([{ message: "Something went wrong!", speaker: "bot", subject: "", createdAt: new Date() }]));
              dispatch(setIsDisabled(false));
            })
            .finally(() => {
              dispatch(setIsFetchManagement(true));
            });
        } else {
          dispatch(removeAndAddMessages([{ message: "Something went wrong!", speaker: "bot", subject: "", createdAt: new Date() }]));
          dispatch(setIsDisabled(false));
        }
      });

    dispatch(setReplies(INITIAL_REPLIES));
  };

  const handleResponse = (res) => {
    const resSubject = res?.data?.subject;
    const resContext = res?.data?.context?.[0];
    let imageUrls;

    if (resSubject === "photos") {
      imageUrls = res?.data?.images_url || {};
    } else {
      imageUrls = res?.data?.images_url?.length > 0 ? JSON.parse(res?.data?.images_url) : [];
    }

    if (resSubject === "schedule a tour") {
      const tourType = resContext?.tourType || labelItems.appointment_type;

      const tourOptions = {
        in_person_tour: isInPersonTourEnabled,
        live_video_tour: isVideoTourEnabled,
        self_guided_tour: isSelfGuidedTourEnabled,
      };

      setTimeout(() => {
        for (const [appointmentType, isEnabled] of Object.entries(tourOptions)) {
          if (isEnabled) {
            handleTourResponse(res, false, tourType || appointmentType);
            break;
          }
        }
      }, 0);

      dispatch(setShouldFetchSlots(false));
    }

    if (resSubject === "unit availability") {
      const payloadT = { bedroom_type: `${resContext?.bedroomPreference}` };

      if (resContext?.priceRange) {
        payloadT["price"] = resContext?.priceRange.split("-");
      }

      if (resContext?.desiredMoveInDate) {
        payloadT["desired_move_in"] = resContext?.desiredMoveInDate;
      }

      localStorage.setItem(`${propertyId}-context`, JSON.stringify([resContext]));
      dispatch(setLabelItems(payloadT));
    }

    const linksHandle = resSubject === "schedule a tour" || resSubject === "unit availability" ? res.data.response?.description : res.data.response;

    let isUnitAvailable = resContext?.bedroomPreference && resContext?.desiredMoveInDate && res.data?.response?.available_slots?.floor_plans?.length > 0;

    if (isPriceFilter) {
      isUnitAvailable = isUnitAvailable && resContext?.priceRange;
    }

    // If intent is "apartment_inquiry" then don't ask follow ups, show the units straight up
    if (resContext?.intent === "apartment_inquiry") {
      isUnitAvailable = true;
    }

    if (isUnitAvailable) {
      dispatch(setSearchResult(res?.data?.search_result || false));
    }

    const isContact = resSubject.includes("contact") || resSubject.includes("contact_summary");

    if (optionsBySubject[resSubject]) {
      dispatch(
        setAMAReply({
          description: res.data.response?.description ?? null,
          message: linksHandle,
          id: res.data.message_id,
          summary: res.data.neighborhood_summary,
          speaker: "faqBot",
          subject: resSubject,
          context: res.data.context,
          createdAt: new Date(),
          available_tourTypes: res.data?.available_tourTypes,
          not_available: res.data.response?.available_slots?.filter_excluded,
          follow_up: res.data?.follow_up,
          availableUnits: isUnitAvailable ? res.data.response?.available_slots : null,
          images: imageUrls,
          photos: imageUrls,
        })
      );
    } else if (isContact) {
      const entryId = localStorage.getItem(`${propertyId}-entry_id`);

      if (entryId) {
        dispatch(
          removeAndAddMessages([
            {
              message: t("already_filled_contact_form"),
              id: res.data.message_id,
              speaker: "faqBot",
              createdAt: new Date(),
            },
          ])
        );

        dispatch(setReplies(INITIAL_REPLIES));
        dispatch(setIsDisabled(false));
      } else {
        dispatch(setIsDisabled(false));

        dispatch(
          removeAndAddMessages([
            {
              message: t("leasing_agent_will_contact"),
              speaker: "bot",
              createdAt: new Date().toISOString(),
            },
          ])
        );

        let rep = {
          checkStorage: true,
          disableResetBox: false,
          options: FORM_4_CONTACT_US_OPTIONS,
          optionsType: "Form4ContactUs",
          title: "Enter First Name and Last Name",
          renderType: "multiInputView",
        };

        dispatch(setReplies(rep));
      }
    } else {
      dispatch(
        removeAndAddMessages([
          {
            message: linksHandle,
            id: res.data.message_id,
            summary: res.data.neighborhood_summary,
            speaker: "faqBot",
            subject: resSubject,
            createdAt: new Date(),
            available_tourTypes: res.data.available_tourTypes,
            follow_up: res.data?.follow_up,
            images: imageUrls,
          },
        ])
      );

      dispatch(setIsDisabled(false));
    }

    if (res?.data?.performance_logger?.[0]?.table && isPerformanceReport && reportIds.includes(propertyId)) {
      dispatch(addNewMessage([{ message: "performanceReportText", speaker: "bot", createdAt: new Date() }]));
      dispatch(addNewMessage([{ key: "report", speaker: "comp", compData: res?.data?.performance_logger?.[0]?.table, createdAt: new Date() }]));
    }

    // If subject is an array then take first element
    const subject = Array.isArray(resSubject) ? resSubject[0] : resSubject;

    // Increment only if `subject` is NOT in `FOLLOW_UP_KEYS`
    const hasFollowUpKey = FOLLOW_UP_KEYS.includes(subject);
    let storedCount = localStorage.getItem(`${propertyId}-follow-up-count`);

    if (!hasFollowUpKey) {
      storedCount = (parseInt(storedCount) || 0) + 1;
      localStorage.setItem(`${propertyId}-follow-up-count`, storedCount);
    }

    if (parseInt(storedCount) >= 3 && !ALL_CHAT_SUBJECTS.filter((o) => o !== "schedule").includes(subject)) {
      getScheduleStatus().then((alreadyScheduled) => {
        if (alreadyScheduled === false) {
          localStorage.setItem(`${propertyId}-follow-up-count`, 0);

          dispatch(
            addNewMessage([
              {
                message: "wouldYouSchedule",
                id: res.data.message_id,
                speaker: "faqBot",
                subject: "schedule",
                createdAt: new Date(),
                follow_up: res.data.follow_up,
              },
            ])
          );
        }
      });
    }
  };

  const onClickSend = () => {
    if (propertyId && !disabled && !optionDisabled) {
      dispatch(setShowAllOptions(false));
      onFAQs(propertyId);
    }
  };

  const onKeyPress = (e) => {
    if (e.key === "Enter" && !disabled && !optionDisabled) {
      onClickSend();
    }
  };

  const onClickExploreMore = () => {
    if (!optionDisabled) {
      dispatch(setShowAllOptions(!showAllOptions));
    }
  };

  return (
    <>
      {showAllOptions ? (
        <div>
          <OptionsBox fromAMA={true} />
        </div>
      ) : null}

      <div className="hayley-flex hayley-justify-between hayley-p-4">
        <div
          className={`hayley-flex hayley-h-12 hayley-w-full hayley-items-center hayley-justify-between hayley-rounded-3xl hayley-border hayley-border-solid hayley-border-custom-6 hayley-pl-4 hayley-pr-3`}
        >
          <span className="hayley-w-full">
            <Input
              data-testid="ama-input"
              className={`hayley-w-full hayley-border-none focus:hayley-outline-none`}
              type={"text"}
              placeholder={`${t("enterQuesHere")}`}
              disabled={false}
              value={inputValue}
              onChange={onChange}
              onKeyDown={onKeyPress}
              disableUnderline
            />
          </span>
          {/* <CustomTooltip title={t("exploreMore")} placement="top"> */}
          <Icon
            iconType="linearSend"
            className="hayley-cursor-pointer"
            onClick={onClickSend}
            opacity={inputValue?.length > 0 && !disabled && !optionDisabled ? "1" : "0.5"}
          />
          {/* </CustomTooltip> */}
        </div>

        <CustomTooltip title={showAllOptions || optionDisabled ? "" : t("exploreMore")} placement="top">
          <span
            className={`hayley-flex hayley-h-12 hayley-w-12 hayley-items-center hayley-justify-center hayley-overflow-hidden ${
              optionDisabled ? "hayley-opacity-50" : "hayley-cursor-pointer"
            }`}
            onClick={onClickExploreMore}
          >
            {showAllOptions ? (
              <Close className="hayley-h-6 hayley-w-6" sx={{ "&:hover": { color: `${backgroundColor} !important` } }} />
            ) : (
              <Icon iconType={"moreIcon"} className="" />
            )}
          </span>
        </CustomTooltip>
      </div>
    </>
  );
};

export default AMAOptions;
